import { logger } from 'src/logger';
import { getCurrentTime, getTimeDifference } from 'src/utilities/DateTimeUtilities';
import { TicketsParsedDataItem, BarResDataSeries, LineAppDataSeries, PieChartDataSeries, TicketGraphsData } from '../tickets/TicketsInterfaces';
import {
  countByMonthBar,
  createTicketGraphData,
  TicketResolutionDataProcess,
  TicketStatusDataProcess,
  getTicketByApplicationSeries
} from '../tickets/TicketsDataProcessing';
import { BusinessReportingFilters } from './BusinessReportingSelectionFilters';
import { aggregateBusinessReportingData } from './business-reporting-aggregator-utils/BusinessReportingTicketAggregator';
import {
  getTop5ResolutionTypesByTicketCount,
  getTop5ResolutionTypesByTimeSpent,
  getTop5ApplicationChartData
} from './business-reporting-aggregator-utils/BusinessReportingChartsAggregator';
import { BusinessReportingAggregatedResult } from './BusinessReportingInterfaces';
import { createEmptyBusinessReportingAggregatedData, createEmptyChartData, TICKET_STATUS } from './BusinessReportingConstants';
import { createBusinessReportingMasterFilterData, FilterData } from './BusinessReportingUtils';

export interface ProcessedBusinessReportingData {
  aggregatedData: BusinessReportingAggregatedResult;
  severityChartData: (BarResDataSeries | LineAppDataSeries)[];
  severityChartXDomain: string[];
  severityChartYDomain: number;
  applicationChartData: LineAppDataSeries[];
  applicationChartXDomain: string[];
  applicationChartYDomain: number[];
  incomingChartData: BarResDataSeries[];
  incomingChartXDomain: string[];
  incomingChartYDomain: number[];
  resolutionTypeCountPieData: PieChartDataSeries[];
  resolutionTypeTimeSpentPieData: PieChartDataSeries[];
  processedFilters: string;
}

/**
 * Processes chart data for business reporting
 * Leverages existing ticket processing functions for consistency
 * @param filteredTicketData - Filtered ticket data
 * @returns Chart data for all business reporting charts
 */
export const processChartData = (filteredTicketData: TicketsParsedDataItem[]) => {
  try {
    const calculatedGraphData = createTicketGraphData(filteredTicketData);
    const severityGraph = createMixedSeverityChartData(calculatedGraphData);
    const { dataSeries: severityData, XDomain: severityXDomain, YDomain: severityYDomain } = severityGraph;

    // Process application data using the existing function
    const applicationData = getTicketByApplicationSeries(calculatedGraphData.applicationTickets);
    const applicationChartData = getTop5ApplicationChartData(applicationData.dataSeries);
    const applicationChartXDomain = applicationData.XDomain;
    const applicationChartYDomain = [0, applicationData.YDomain];

    const incomingTicketsProcessed = TicketStatusDataProcess(calculatedGraphData.incomingTickets, true);
    const filteredIncomingSeries = incomingTicketsProcessed.dataSeries.filter(
      (series) => series.title === TICKET_STATUS.RESOLVED || series.title === 'Total'
    );

    return {
      severityChartData: severityData,
      severityChartXDomain: severityXDomain,
      severityChartYDomain: severityYDomain,
      applicationChartData: applicationChartData,
      applicationChartXDomain: applicationChartXDomain,
      applicationChartYDomain: applicationChartYDomain,
      incomingChartData: filteredIncomingSeries,
      incomingChartXDomain: incomingTicketsProcessed.xDomain,
      incomingChartYDomain: [0, incomingTicketsProcessed.yDomain]
    };
  } catch (error) {
    logger.error('Error processing chart data', error as Error);
    return createEmptyChartData();
  }
};

/**
 * Creates mixed chart data with severity bars and resolved tickets line
 * @param calculatedGraphData - Processed ticket graph data
 * @returns Mixed chart data with stacked bars and line series
 */
const createMixedSeverityChartData = (calculatedGraphData: TicketGraphsData) => {
  try {
    const severityGraph = countByMonthBar(calculatedGraphData.severityTickets);
    const incomingTicketsProcessed = TicketStatusDataProcess(calculatedGraphData.incomingTickets, true);
    // Extract the resolved ticket series
    const resolvedSeries = incomingTicketsProcessed.dataSeries.find((series) => series.title === TICKET_STATUS.RESOLVED);
    if (resolvedSeries) {
      const resolvedLine = {
        ...resolvedSeries,
        type: 'line' as const,
        title: 'Resolved Tickets',
        color: '#8B4513' // Brown color
      };
      const combinedSeries = [...severityGraph.dataSeries, resolvedLine];

      const resolvedMaxValue = Math.max(...resolvedLine.data.map((point) => point.y));
      const severityMaxValue = severityGraph.YDomain;
      // Using the scaling max value * 1.1 for padding
      const calculatedYDomain = Math.max(resolvedMaxValue, severityMaxValue) * 1.1;

      return {
        dataSeries: combinedSeries,
        XDomain: severityGraph.XDomain,
        YDomain: calculatedYDomain
      };
    }
    // Fallback to original severity data if no resolved series found
    return severityGraph;
  } catch (error) {
    logger.error('Error creating mixed severity chart data', error as Error);
    // Fallback to original severity processing
    return countByMonthBar(calculatedGraphData.severityTickets);
  }
};

/**
 * Main function to process all business reporting ticket data
 * Similar to createUserMetricsGraphDataV2 but for business reporting
 * @param rawTicketData - Raw ticket data from finOpsContext
 * @param filters - Business reporting filters
 * @returns Complete processed data for business reporting
 */
export const processBusinessReportingTicketData = (
  filteredTicketData: TicketsParsedDataItem[],
  filters: BusinessReportingFilters
): ProcessedBusinessReportingData => {
  try {
    const start = getCurrentTime();

    if (filteredTicketData.length === 0) {
      logger.warn('No raw ticket data available for business reporting');
      return getEmptyProcessedData(filters);
    }

    const businessReportingResult = aggregateBusinessReportingData(filteredTicketData);
    const chartData = processChartData(filteredTicketData);
    const resolutionTypesByCount = getTop5ResolutionTypesByTicketCount(businessReportingResult.entries);
    const resolutionTypesByTimeSpent = getTop5ResolutionTypesByTimeSpent(filteredTicketData);

    const processedFilters = JSON.stringify({
      applications: filters.applications,
      rollupPeriod: filters.rollupPeriod,
      period: filters.period
    });

    const elapsed = getTimeDifference(start);
    logger.info(`Processed business reporting ticket data in ${elapsed} ms`, {
      inputRecords: filteredTicketData.length,
      filteredRecords: filteredTicketData.length,
      outputRecords: businessReportingResult.entries.length
    });

    return {
      aggregatedData: businessReportingResult,
      ...chartData,
      resolutionTypeCountPieData: resolutionTypesByCount,
      resolutionTypeTimeSpentPieData: resolutionTypesByTimeSpent,
      processedFilters
    };
  } catch (error) {
    logger.error('Unable to process business reporting ticket data', error as Error);
    return getEmptyProcessedData(filters);
  }
};

/**
 * Returns empty processed data structure for error/empty states
 * @param filters - Business reporting filters for the processed filters string
 * @returns Empty processed data structure
 */
const getEmptyProcessedData = (filters: BusinessReportingFilters): ProcessedBusinessReportingData => {
  const processedFilters = JSON.stringify({
    applications: filters.applications,
    rollupPeriod: filters.rollupPeriod,
    period: filters.period
  });

  return {
    aggregatedData: createEmptyBusinessReportingAggregatedData(),
    ...createEmptyChartData(),
    processedFilters
  };
};

/**
 * Creates filters for business reporting data
 * @param ticketData - Processed ticket data
 * @returns Business reporting filter options
 */
export const createTicketMasterFilterData = (ticketData: TicketsParsedDataItem[]): FilterData => {
  return createBusinessReportingMasterFilterData(ticketData, {
    getApplicationField: (item) => item.application_name,
    getDateField: (item) => item.create_date_UserTimeZone_formatted,
    logContext: 'ticket data'
  });
};
